import moment from "moment";
import jwtDecode from "jwt-decode";
import _isEmpty from "lodash/isEmpty";
import _isString from "lodash/isString";

import { oktaAuth } from "../../config/okta";
import { LO_STO, SESSION_STO } from "../../util/constants";

const REFRESH_FREQUENCY_IN_MIN = 20;
const EXPIRY_LIMIT_IN_MINUTES = 120; // user session is limited: 2 hours (120 m)

const login = (password, email, redirectRoute) => {
  return new Promise((resolve, reject) => {
    oktaAuth
      .signInWithCredentials({ password, username: email })
      .then((res) => {
        const { status, sessionToken } = res;

        localStorage.removeItem(LO_STO.LOGOUT_MESSAGE);

        if (!_isEmpty(sessionToken) && status === "SUCCESS") {
          window.sessionStorage.setItem(SESSION_STO.SESSION_TOKEN, res.sessionToken);
          window.localStorage.setItem(LO_STO.LOGIN_TIME, moment());

          oktaAuth.signInWithRedirect({
            originalUri: redirectRoute || "/dashboard",
            sessionToken,
            responseType: ["id_token", "token"],
            responseMode: "fragment",
            scopes: ["openid", "email", "profile"]
          });
          resolve(res);
        } else {
          console.error("Authentication error:", res);
          reject(res);
        }
      })
      .catch((error) => {
        console.error("Authentication Error:", error);
        reject(error);
      });
  });
};

const initializeUser = async (oktaAuth) => {
  try {
    const idToken = await oktaAuth.tokenManager.get("idToken");
    const user = await oktaAuth.getUser();
    localStorage.setItem(LO_STO.JWT_TOKEN, idToken?.idToken);
    localStorage.setItem(LO_STO.OKTA_USER, JSON.stringify(user));
  } catch {
    console.log("Error initializing user!");
    logoutUser("Error initializing user!");
  }
};

// JWT token refreshes every REFRESH_FREQUENCY_IN_MIN mins
// If session is longer then EXPIRY_LIMIT_IN_MINUTES -> logout automatically
const startRefreshTimer = async () => {
  const jwtToken = window.localStorage.getItem(LO_STO.JWT_TOKEN);
  if (!jwtToken) return;

  const pathname = window.location.pathname;
  localStorage.setItem(LO_STO.CURRENT_PATH, pathname);

  const decodedJwt = jwtDecode(jwtToken);
  const expiry = decodedJwt.exp;
  const m = moment.unix(expiry);
  console.info("[User:%s] Auth token expires %s at %s (exp %d)", decodedJwt.userID, m.fromNow(), m.format(), expiry);

  await verifyToken();

  // If the token is close to expiring, refresh it immediately
  if (isCloseToExpiring(decodedJwt)) {
    // Refresh immediately
    console.info(
      "[User:%s] Auth token expiry already below threshold, refreshing token immediately",
      decodedJwt.userID
    );
    refreshToken();
  }

  // Refresh the token automatically regularly
  //  console.info('[User:%s] Starting timer to trigger refresh token in %d min at around %s (exp %d)', decoded.userID, REFRESH_FREQUENCY_IN_MIN, moment().add(REFRESH_FREQUENCY_IN_MIN, 'minutes').format(), expiry)
  return setInterval(verifyAndRefreshToken, REFRESH_FREQUENCY_IN_MIN * 60 * 1000);
};

const verifyAndRefreshToken = async () => {
  const isTokenValidToRefresh = await verifyToken();
  isTokenValidToRefresh && refreshToken();
};

const verifyToken = async () => {
  const jwtToken = window.localStorage.getItem(LO_STO.JWT_TOKEN);
  const decodedJwt = jwtDecode(jwtToken);

  const isTokenExpired = moment.unix(decodedJwt.exp).diff(moment(), "minutes") <= 0;

  // If token has expired for more than 2 hours, log the user out/redirect them to the login page
  if (isSessionExpired() || !jwtToken || isTokenExpired) {
    console.info("[User:%s] Session expired, logging out immediately", decodedJwt.userID);
    logoutUser("Session expired, logging out immediately.");
    return false;
  }

  return true;
};

const refreshToken = () => {
  try {
    oktaAuth.tokenManager.renew("idToken").then((newToken) => {
      if (newToken?.idToken) localStorage.setItem(LO_STO.JWT_TOKEN, newToken?.idToken);
      else logoutUser();
    });
    oktaAuth.tokenManager.renew("accessToken");
  } catch (e) {
    console.info("Token refresh failed", e);
    logoutUser("Token refresh failed.");
  }
};

const isCloseToExpiring = (decodedJwt) => {
  return moment.unix(decodedJwt.exp).diff(moment(), "minutes") <= REFRESH_FREQUENCY_IN_MIN;
};

const isSessionExpired = () => {
  const currentTime = moment();
  const loginTime = moment(window.localStorage.getItem(LO_STO.LOGIN_TIME));
  const timeDifference = currentTime.diff(loginTime, "minutes");

  return timeDifference >= EXPIRY_LIMIT_IN_MINUTES - 1; // check in 1 minute before expiration time
};

const isEmailVerified = () => {
  const oktaUser = window.localStorage.getItem(LO_STO.OKTA_USER);
  const parsedOktaUser = JSON.parse(oktaUser);

  return parsedOktaUser?.email_verified || false;
};

const logoutUser = (logoutMessage) => {
  oktaAuth
    .signOut()
    .then(() => {
      window.location.href = "/";
      window.localStorage.clear();
      window.sessionStorage.clear();
    })
    .then(() => {
      _isString(logoutMessage) &&
        !_isEmpty(logoutMessage) &&
        localStorage.setItem(LO_STO.LOGOUT_MESSAGE, logoutMessage);
    });
};

export { initializeUser, isEmailVerified, login, logoutUser, startRefreshTimer };
